import React from 'react';
import { havcCancelButton, havcDangerButton, havcNeutralButton } from './havcTheme';
import {
  Button,
  Box,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Fade,
  CircularProgress
} from '@mui/material';
import axios from 'src/utils/axios';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Modern stil tanımları
const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden',
    },
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: '#ffffff',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogContent: {
    padding: '32px 24px',
    backgroundColor: '#ffffff',
    textAlign: 'center',
  },
  warningIcon: {
    fontSize: '64px',
    color: '#f59e0b',
    marginBottom: '16px',
  },
  errorIcon: {
    fontSize: '64px',
    color: '#ef4444',
    marginBottom: '16px',
  },
  dialogText: {
    color: '#475569',
    fontSize: '1rem',
    lineHeight: 1.6,
    marginBottom: '8px',
  },
  errorText: {
    color: '#ef4444',
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.6,
    backgroundColor: '#fef2f2',
    padding: '16px',
    borderRadius: '12px',
    border: '1px solid #fecaca',
  },
  dialogActions: {
    padding: '16px 24px 24px',
    backgroundColor: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    gap: '12px',
    justifyContent: 'center',
  },
  confirmButton: {
    borderRadius: '12px',
    padding: '12px 32px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    backgroundColor: '#ef4444',
    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
    '&:hover': {
      backgroundColor: '#dc2626',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
    },
    '&:disabled': {
      backgroundColor: '#fca5a5',
    },
  },
  cancelButton: {
    borderRadius: '12px',
    padding: '12px 32px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    borderColor: '#e2e8f0',
    color: '#64748b',
    '&:hover': {
      backgroundColor: '#f1f5f9',
      borderColor: '#cbd5e1',
    },
  },
  closeButton: {
    borderRadius: '12px',
    padding: '12px 32px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    backgroundColor: '#64748b',
    '&:hover': {
      backgroundColor: '#475569',
    },
  },
};

function DeleteProjectDialog({ open, setOpen, ProjectID, sorgula }) {
  const [dialogContextText, setDialogContextText] = React.useState(
    'Bu projeyi silmek istediğinize emin misiniz?'
  );
  const [isError, setIsError] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    axios
      .post(`deleteHavcProject/${ProjectID}`)
      .then(response => {
        if (response.data.includes('HATA-')) {
          const errorMessage = response.data.split('HATA-')[1];
          setDialogContextText(errorMessage);
          setIsError(true);
          return;
        }
        setIsError(false);
        setOpen(false);
        sorgula();
      })
      .catch(() => {
        setDialogContextText('Silme işlemi sırasında bir hata oluştu.');
        setIsError(true);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleClose = () => {
    setDialogContextText("Bu projeyi silmek istediğinize emin misiniz?");
    setIsError(false);
    setOpen(false);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={() => !isDeleting && setOpen(false)}
      aria-labelledby="delete-dialog-title"
      sx={styles.dialog}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleIcon}>
          <DeleteOutlineIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Proje Silme
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
            HVAC ve Enerji Çözümleri
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        {isError ? (
          <Fade in timeout={300}>
            <Box>
              <ErrorOutlineIcon sx={styles.errorIcon} />
              <Typography sx={styles.errorText}>
                {dialogContextText}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Fade in timeout={300}>
            <Box>
              <WarningAmberIcon sx={styles.warningIcon} />
              <Typography sx={styles.dialogText}>
                {dialogContextText}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                Bu işlem geri alınamaz.
              </Typography>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        {isError === false ? (
          <>
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              sx={havcCancelButton}
              disabled={isDeleting}
            >
              Hayır, Vazgeç
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              sx={havcDangerButton}
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            >
              {isDeleting ? 'Siliniyor...' : 'Evet, Sil'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={havcNeutralButton}
            endIcon={<CloseIcon />}
          >
            Kapat
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DeleteProjectDialog;
